import { useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Cast, ScreenShare } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState, SectionHeader } from '@pixlide/ui';
import type { components } from '@/api/generated/schema';
import { formatDate } from '@/lib/format';
import { useBroadcasts, useProjects, useScreens } from '@/api/queries/dashboard';
import { useBroadcast, useChangeBroadcastState, type BroadcastTransition } from '@/api/queries/broadcasts';
import { useHasPermission } from '@/stores/auth-store';
import { BroadcastForm } from '@/features/diffusion/BroadcastForm';
import { describeBroadcastError } from '@/features/diffusion/broadcast-errors';
import { BROADCAST_STATUS, TRANSITION, describeDays, describeWindow } from '@/features/diffusion/broadcast-model';

type BroadcastItem = components['schemas']['PixlideApplicationFeaturesBroadcastsBroadcastListItem'];

/** Statuts côté Domain : 0 brouillon, 1 programmée, 2 en pause, 3 terminée. */
const STATUS_DRAFT = 0;
const STATUS_SCHEDULED = 1;
const STATUS_PAUSED = 2;
const STATUS_ENDED = 3;

/** Seuil sous lequel une diffusion est « prioritaire » (`Broadcast.DefaultPriority` = 100). */
const DEFAULT_PRIORITY = 100;

type Mode = { kind: 'create' } | { kind: 'edit'; broadcastId: string } | null;

function ChoiceCard({
  icon,
  title,
  description,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    // Un vrai <button> : la carte est une action, elle doit être atteignable au clavier et
    // annoncée comme telle.
    <button type="button" onClick={onClick} disabled={disabled} className="text-left disabled:cursor-not-allowed disabled:opacity-60">
      <Card raised className="flex h-full flex-col gap-6 p-8 transition hover:-translate-y-0.5 sm:p-9">
        <span aria-hidden className="text-ink-900">
          {icon}
        </span>
        <div>
          <h3 className="max-w-sm text-2xl leading-tight font-medium text-ink-900">{title}</h3>
          <p className="mt-3 max-w-sm text-ink-500">{description}</p>
        </div>
      </Card>
    </button>
  );
}

function describeSchedule(broadcast: BroadcastItem): string {
  const period = broadcast.endsOn
    ? `Du ${formatDate(broadcast.startsOn)} au ${formatDate(broadcast.endsOn)}`
    : `À partir du ${formatDate(broadcast.startsOn)}`;

  return `${period} · ${describeDays(broadcast.days)} · ${describeWindow(broadcast.startsAt, broadcast.endsAt)}`;
}

/**
 * Diffusion : programmer les vidéos créées dans Pixlide sur les écrans appairés, puis les
 * suspendre, les reprendre, les modifier ou les arrêter.
 *
 * Une diffusion programmée part sur les écrans en quelques secondes : l'API publie un signal
 * que la passerelle de diffusion relaie aux écrans visés, qui rechargent leur playlist.
 */
export function DiffusionPage() {
  const [mode, setMode] = useState<Mode>(null);
  const [confirmingEnd, setConfirmingEnd] = useState<string | null>(null);
  const listRef = useRef<HTMLElement>(null);

  const broadcasts = useBroadcasts({ pageSize: 50 });
  const projects = useProjects({ pageSize: 100 });
  const screens = useScreens({ pageSize: 100 });
  const editing = useBroadcast(mode?.kind === 'edit' ? mode.broadcastId : null);
  const changeState = useChangeBroadcastState();

  // Masquer un bouton n'est qu'un confort : c'est l'API qui fait respecter ces droits.
  const canWrite = useHasPermission('broadcasts:write');
  const canPublish = useHasPermission('broadcasts:publish');

  const transition = async (broadcastId: string, value: BroadcastTransition) => {
    try {
      await changeState.mutateAsync({ broadcastId, transition: value });
    } catch {
      // Affiché via changeState.error.
    } finally {
      setConfirmingEnd(null);
    }
  };

  const formReady = projects.data !== undefined && screens.data !== undefined;
  const closeForm = () => setMode(null);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-10">
      <section>
        {mode === null ? null : (
          <button
            type="button"
            onClick={closeForm}
            className="mb-3 flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900"
          >
            <ArrowLeft aria-hidden className="size-4" />
            Retour aux diffusions
          </button>
        )}
        <h1 className="text-4xl font-medium tracking-tight">Diffusion</h1>
        <p className="mt-3 text-lg text-ink-700">
          Choisissez votre projet à diffuser et renseignez les informations de diffusion.
        </p>
      </section>

      {/* Le parcours de programmation occupe tout l'écran, comme sur la maquette : l'accueil
          et la liste reviennent à sa fermeture. */}
      {mode !== null && !formReady ? <LoadingState label="Chargement de vos vidéos et écrans…" /> : null}

      {mode?.kind === 'create' && formReady ? (
        <BroadcastForm
          projects={projects.data.items}
          screens={screens.data.items}
          onDone={closeForm}
          onCancel={closeForm}
        />
      ) : null}

      {mode?.kind === 'edit' && formReady && editing.data ? (
        <BroadcastForm
          // La clé force un formulaire neuf par diffusion : ses valeurs par défaut ne sont
          // lues qu'au montage.
          key={mode.broadcastId}
          projects={projects.data.items}
          screens={screens.data.items}
          existing={{ summary: editing.data.summary, screenIds: editing.data.screens.map((screen) => screen.screenId) }}
          onDone={closeForm}
          onCancel={closeForm}
        />
      ) : null}

      {mode === null ? (
        <>
          <section aria-labelledby="choices-heading">
            <h2 id="choices-heading" className="sr-only">
              Que souhaitez-vous faire ?
            </h2>
            <div className="grid gap-5 lg:grid-cols-2">
              <ChoiceCard
                icon={<Cast className="size-16" strokeWidth={1.25} />}
                title="Mettre en place une nouvelle diffusion"
                description="Établissez dès maintenant vos prochaines diffusions à venir"
                disabled={!canWrite}
                onClick={() => setMode({ kind: 'create' })}
              />
              <ChoiceCard
                icon={<ScreenShare className="size-16" strokeWidth={1.25} />}
                title="Modifier une diffusion existante ou en cours"
                description="Besoin d'annuler, retirer et modifier une diffusion prévue ou en cours"
                onClick={() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              />
            </div>
          </section>

          <section aria-labelledby="broadcasts-heading" ref={listRef} className="scroll-mt-6">
            <SectionHeader title="Diffusions programmées" />
            <h2 id="broadcasts-heading" className="sr-only">
              Diffusions programmées
            </h2>

            {changeState.error ? (
              <p role="alert" className="mb-3 rounded-xl bg-danger-100 px-4 py-3 text-sm font-medium text-danger-500">
                {describeBroadcastError(changeState.error)}
              </p>
            ) : null}

            {broadcasts.isPending ? <LoadingState /> : null}

            {broadcasts.isError ? (
              <ErrorState
                message="Impossible de charger vos diffusions pour le moment."
                onRetry={() => void broadcasts.refetch()}
              />
            ) : null}

            {broadcasts.data?.items.length === 0 ? (
              <EmptyState
                icon={<Cast className="size-10" strokeWidth={1.25} />}
                title="Aucune diffusion programmée"
                description="Sélectionnez une vidéo pour la programmer sur un ou plusieurs de vos écrans."
                action={
                  canWrite ? <Button onClick={() => setMode({ kind: 'create' })}>Programmer une diffusion</Button> : undefined
                }
              />
            ) : null}

            {broadcasts.data && broadcasts.data.items.length > 0 ? (
              <Card className="divide-y divide-ink-100/70">
                {broadcasts.data.items.map((broadcast) => {
                  const status = BROADCAST_STATUS[broadcast.status] ?? BROADCAST_STATUS[STATUS_DRAFT]!;
                  const isEnded = broadcast.status === STATUS_ENDED;
                  const isBusy = changeState.isPending && changeState.variables?.broadcastId === broadcast.id;

                  return (
                    <div key={broadcast.id} className={`flex flex-wrap items-center gap-4 px-5 py-4 ${isEnded ? 'opacity-60' : ''}`}>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 truncate font-medium">
                          {broadcast.name || broadcast.videoProjectName}
                          {broadcast.priority < DEFAULT_PRIORITY ? <Badge tone="brand">Prioritaire</Badge> : null}
                        </p>
                        <p className="truncate text-sm text-ink-500">
                          {broadcast.videoProjectName} · {broadcast.screenCount} écran{broadcast.screenCount > 1 ? 's' : ''}
                        </p>
                        <p className="truncate text-xs text-ink-300">{describeSchedule(broadcast)}</p>
                      </div>

                      <Badge tone={status.tone}>{status.label}</Badge>

                      {isEnded ? null : (
                        <div className="flex flex-wrap justify-end gap-2">
                          {canWrite ? (
                            <Button size="sm" variant="ghost" onClick={() => setMode({ kind: 'edit', broadcastId: broadcast.id })}>
                              Modifier
                            </Button>
                          ) : null}

                          {canPublish && broadcast.status === STATUS_DRAFT ? (
                            <Button size="sm" variant="secondary" isLoading={isBusy} onClick={() => void transition(broadcast.id, TRANSITION.publish)}>
                              Publier
                            </Button>
                          ) : null}

                          {canPublish && broadcast.status === STATUS_SCHEDULED ? (
                            <Button size="sm" variant="secondary" isLoading={isBusy} onClick={() => void transition(broadcast.id, TRANSITION.pause)}>
                              Mettre en pause
                            </Button>
                          ) : null}

                          {canPublish && broadcast.status === STATUS_PAUSED ? (
                            <Button size="sm" variant="secondary" isLoading={isBusy} onClick={() => void transition(broadcast.id, TRANSITION.resume)}>
                              Reprendre
                            </Button>
                          ) : null}

                          {canPublish ? (
                            confirmingEnd === broadcast.id ? (
                              // Arrêter est DÉFINITIF (une diffusion terminée ne se relance pas) :
                              // confirmation en deux temps, la pause restant l'option réversible.
                            <>
                                <Button size="sm" variant="danger" isLoading={isBusy} onClick={() => void transition(broadcast.id, TRANSITION.end)}>
                                  Confirmer l'arrêt
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setConfirmingEnd(null)}>
                                  Annuler
                                </Button>
                            </>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => setConfirmingEnd(broadcast.id)}>
                                Arrêter
                              </Button>
                            )
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Card>
            ) : null}
          </section>

          <p className="text-sm text-ink-500">
            Besoin d'aide pour votre première diffusion ?{' '}
            <Link to="/support" className="font-medium text-brand-700 hover:underline">
              Consultez le support
            </Link>
            .
          </p>
        </>
      ) : null}
    </div>
  );
}
